import { create } from '@bufbuild/protobuf'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import {
  createConnectQueryKey,
  useMutation,
  useQuery,
} from '@connectrpc/connect-query'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  createContractor,
  deleteContractor,
  listContractors,
  updateContractor,
} from '@ebalance/proto/bffuser/v1/contractor_service-ContractorService_connectquery'
import { ListContractorsRequestSchema } from '@ebalance/proto/bffuser/v1/contractor_service_pb'
import {
  convertProspectToContract,
  createCustomerSupplyPoint,
  deleteCustomerSupplyPoint,
  getContractTrend,
  getCustomerSupplyPointDeletability,
  getCustomerSupplyPointFormOptions,
  listCustomerSupplyPoints,
  listCustomers,
  markProspectLost,
  updateCustomer,
  updateCustomerSupplyPoint,
} from '@ebalance/proto/bffuser/v1/customer_service-CustomerService_connectquery'
import {
  GetContractTrendRequestSchema,
  type GetContractTrendResponse,
  GetCustomerSupplyPointDeletabilityRequestSchema,
  GetCustomerSupplyPointFormOptionsRequestSchema,
  ListCustomerSupplyPointsRequestSchema,
  ListCustomersRequestSchema,
} from '@ebalance/proto/bffuser/v1/customer_service_pb'
import { DateSchema } from '@ebalance/proto/google/type/date_pb'
import { type UseQueryResult, useQueryClient } from '@tanstack/react-query'

import type { CivilDate } from '~/models/civilDate'
import {
  toContractorRow,
  toConvertRequest,
  toCreateRequest,
  toCustomerSummary,
  toDeletability,
  toFormOptions,
  toSupplyPoint,
  toUpdateRequest,
} from '~/pages/CustomerInfo/models/converters'
import {
  toContractorError,
  toSupplyPointError,
} from '~/pages/CustomerInfo/models/serverError'
import { SupplyPointError } from '~/pages/CustomerInfo/models/supplyPointError'
import type {
  ContractorInput,
  ContractorRow,
  ConvertProspectInput,
  CreateSupplyPointInput,
  CustomerSummary,
  Deletability,
  FormOptions,
  SupplyPoint,
  SupplyPointToken,
  UpdateSupplyPointInput,
} from '~/pages/CustomerInfo/models/types'

/**
 * 顧客管理の query / mutation。 画面は proto を読まず、 ここで画面の型 (`../models/types.ts`) に
 * 変換して渡す。 mutation は拒否理由 (`google.rpc.ErrorInfo`) を `SupplyPointError` /
 * `ContractorError` に写して投げる (画面の文言と欄の振り分けはその reason で決まる)。
 *
 * query の失敗は画面内の Alert で知らせるので、 全画面エラーには飛ばさない (`throwOnError: false`)。
 */

/** 画面が使う mutation の形。 `mutateAsync` は失敗時に写し済みのエラーを投げる。 */
export type CustomerInfoMutation<Input, Output = void> = {
  mutateAsync: (input: Input) => Promise<Output>
  isPending: boolean
}

/** 書き込み後は顧客一覧・契約一覧・選択肢・契約者一覧を最新化する。 */
const useInvalidateAll = (): (() => Promise<void>) => {
  const queryClient = useQueryClient()
  return async () => {
    await Promise.all(
      [
        createConnectQueryKey({
          schema: listCustomers,
          cardinality: undefined,
        }),
        createConnectQueryKey({
          schema: listCustomerSupplyPoints,
          cardinality: undefined,
        }),
        createConnectQueryKey({
          schema: getCustomerSupplyPointFormOptions,
          cardinality: undefined,
        }),
        createConnectQueryKey({
          schema: listContractors,
          cardinality: undefined,
        }),
        // 契約推移の日別集計 (エリア・年度範囲ごとの全クエリ)
        createConnectQueryKey({
          schema: getContractTrend,
          cardinality: undefined,
        }),
      ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    )
  }
}

/** 競合時の「再読み込み」: 顧客一覧・契約一覧を取り直す。 */
export const useReloadCustomerInfo = (): (() => Promise<void>) =>
  useInvalidateAll()

export const useListCustomers = (): UseQueryResult<CustomerSummary[], Error> =>
  useQuery(listCustomers, create(ListCustomersRequestSchema, {}), {
    select: (response) => response.customers.map(toCustomerSummary),
    throwOnError: false,
  })

export const useListSupplyPoints = (
  areaCode: AreaCode | undefined,
): UseQueryResult<SupplyPoint[], Error> =>
  useQuery(
    listCustomerSupplyPoints,
    create(
      ListCustomerSupplyPointsRequestSchema,
      areaCode === undefined ? {} : { areaCode },
    ),
    {
      select: (response) => response.supplyPoints.map(toSupplyPoint),
      throwOnError: false,
    },
  )

/** 契約推移の日別集計 (backend の GetContractTrend)。 範囲は暦日 (両端を含む)。 */
export const useGetContractTrend = (
  areaCode: AreaCode | undefined,
  from: CivilDate,
  to: CivilDate,
): UseQueryResult<GetContractTrendResponse, Error> =>
  useQuery(
    getContractTrend,
    create(GetContractTrendRequestSchema, {
      ...(areaCode === undefined ? {} : { areaCode }),
      fromDate: create(DateSchema, from),
      toDate: create(DateSchema, to),
    }),
    { throwOnError: false },
  )

export const useGetFormOptions = (
  areaCode: AreaCode,
  enabled: boolean,
): UseQueryResult<FormOptions, Error> =>
  useQuery(
    getCustomerSupplyPointFormOptions,
    create(GetCustomerSupplyPointFormOptionsRequestSchema, { areaCode }),
    { enabled, select: toFormOptions, throwOnError: false },
  )

/** 編集モーダルを開いた時に取得する削除可否。 キャッシュせず毎回取り直す。 */
export const useGetDeletability = (
  supplyPointId: string,
  enabled: boolean,
): UseQueryResult<Deletability, Error> =>
  useQuery(
    getCustomerSupplyPointDeletability,
    create(GetCustomerSupplyPointDeletabilityRequestSchema, {
      customerSupplyPointId: supplyPointId,
    }),
    {
      enabled,
      gcTime: 0,
      retry: false,
      select: toDeletability,
      throwOnError: false,
    },
  )

export const useCreateSupplyPoint = (): CustomerInfoMutation<
  CreateSupplyPointInput,
  { supplyPointId: string }
> => {
  const invalidate = useInvalidateAll()
  const mutation = useMutation(createCustomerSupplyPoint, {
    onSuccess: invalidate,
  })
  return {
    isPending: mutation.isPending,
    mutateAsync: async (input) => {
      try {
        const response = await mutation.mutateAsync(toCreateRequest(input))
        return { supplyPointId: response.customerSupplyPointId }
      } catch (err) {
        throw toSupplyPointError(err)
      }
    },
  }
}

export const useUpdateSupplyPoint =
  (): CustomerInfoMutation<UpdateSupplyPointInput> => {
    const invalidate = useInvalidateAll()
    const mutation = useMutation(updateCustomerSupplyPoint, {
      onSuccess: invalidate,
    })
    return {
      isPending: mutation.isPending,
      mutateAsync: async (input) => {
        try {
          await mutation.mutateAsync(toUpdateRequest(input))
        } catch (err) {
          throw toSupplyPointError(err)
        }
      },
    }
  }

export const useConvertProspectToContract =
  (): CustomerInfoMutation<ConvertProspectInput> => {
    const invalidate = useInvalidateAll()
    const mutation = useMutation(convertProspectToContract, {
      onSuccess: invalidate,
    })
    return {
      isPending: mutation.isPending,
      mutateAsync: async (input) => {
        try {
          await mutation.mutateAsync(toConvertRequest(input))
        } catch (err) {
          throw toSupplyPointError(err)
        }
      },
    }
  }

export const useMarkProspectLost =
  (): CustomerInfoMutation<SupplyPointToken> => {
    const invalidate = useInvalidateAll()
    const mutation = useMutation(markProspectLost, { onSuccess: invalidate })
    return {
      isPending: mutation.isPending,
      mutateAsync: async (token) => {
        try {
          await mutation.mutateAsync({
            customerSupplyPointId: token.supplyPointId,
            expectedUpdatedAt: token.expectedUpdatedAt,
          })
        } catch (err) {
          throw toSupplyPointError(err)
        }
      },
    }
  }

export const useDeleteSupplyPoint =
  (): CustomerInfoMutation<SupplyPointToken> => {
    const invalidate = useInvalidateAll()
    const mutation = useMutation(deleteCustomerSupplyPoint, {
      onSuccess: invalidate,
    })
    return {
      isPending: mutation.isPending,
      mutateAsync: async (token) => {
        try {
          await mutation.mutateAsync({
            customerSupplyPointId: token.supplyPointId,
            expectedUpdatedAt: token.expectedUpdatedAt,
          })
        } catch (err) {
          throw toSupplyPointError(err)
        }
      },
    }
  }

export const useListContractors = (): UseQueryResult<ContractorRow[], Error> =>
  useQuery(listContractors, create(ListContractorsRequestSchema, {}), {
    select: (response) => response.contractors.map(toContractorRow),
    throwOnError: false,
  })

/**
 * 契約者の追加 (id 未指定) / 編集。 編集は開いた時点の `version` を `expectedUpdatedAt` で送る
 * (楽観ロック)。 競合は SupplyPointError('CONFLICT') で投げる。
 */
export const useSaveContractor = (): CustomerInfoMutation<{
  id: string | undefined
  input: ContractorInput
  expectedUpdatedAt?: Timestamp
}> => {
  const invalidate = useInvalidateAll()
  const creating = useMutation(createContractor, { onSuccess: invalidate })
  const updating = useMutation(updateContractor, { onSuccess: invalidate })
  return {
    isPending: creating.isPending || updating.isPending,
    mutateAsync: async ({ id, input, expectedUpdatedAt }) => {
      try {
        if (id === undefined) {
          await creating.mutateAsync(input)
        } else {
          await updating.mutateAsync({
            contractorId: id,
            ...input,
            expectedUpdatedAt,
          })
        }
      } catch (err) {
        // 競合は需要家の編集と同じ reason (CustomerSupplyPointErrorReason.CONFLICT) で返る
        const conflict = toSupplyPointError(err)
        if (conflict instanceof SupplyPointError) throw conflict
        throw toContractorError(err)
      }
    },
  }
}

export const useDeleteContractor = (): CustomerInfoMutation<string> => {
  const invalidate = useInvalidateAll()
  const mutation = useMutation(deleteContractor, { onSuccess: invalidate })
  return {
    isPending: mutation.isPending,
    mutateAsync: async (contractorId) => {
      try {
        await mutation.mutateAsync({ contractorId })
      } catch (err) {
        throw toContractorError(err)
      }
    },
  }
}

/** 需要家の編集 (社名変更・契約者の付け替え)。 楽観ロックの token は一覧の `version`。 競合は SupplyPointError('CONFLICT')。 */
export const useRenameCustomer = (): CustomerInfoMutation<{
  customerId: string
  customerName: string
  /** 付け替え先の契約者 (未指定 = 変えない)。 */
  contractorId?: string
  expectedUpdatedAt: Timestamp
}> => {
  const invalidate = useInvalidateAll()
  const mutation = useMutation(updateCustomer, { onSuccess: invalidate })
  return {
    isPending: mutation.isPending,
    mutateAsync: async (input) => {
      try {
        await mutation.mutateAsync(input)
      } catch (err) {
        throw toSupplyPointError(err)
      }
    },
  }
}
